import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import s from './styles/ChatMessage.module.scss'
import InitialAvatar from '../../shared/components/InitialAvatar/InitialAvatar'
import { ReactComponent as ArrowLeft } from '../../assets/icons/arrowLeft.svg'
import { ReactComponent as LaptopIcon } from '../../assets/icons/laptop-clock.svg'
// import { ReactComponent as PaperclipIcon } from '../../assets/icons/paperclip.svg'
import { ReactComponent as SendIcon } from '../../assets/icons/send_icon_2.svg'
import { ReactComponent as HeaderIcon } from '../../assets/icons/human-circle.svg'
import { useChatMessage } from './hooks/useChatMessage'
import { navigateToStudentProfile } from 'pages/Dashboard/components/TodaysPrioritySection/utils/priorityCardUi'
import LoaderImg from 'shared/components/LoaderImg/LoaderImg'
import RecommendationAttachment from 'pages/Research/components/RecommendationAttachment/RecommendationAttachment'
import { isValidRecommendationMetadata } from 'pages/Research/utils/buildRecommendationMessage'

function MessageBody({ msg }) {
	const metadata = msg?.metadata
	const hasRichRecommendation = isValidRecommendationMetadata(metadata)

	if (hasRichRecommendation) {
		return (
			<>
				<div
					className={`${s.message__text} ${
						msg.from === 'admin' ? s.admin : ''
					}`}
				>
					{metadata.bodyText}
				</div>
				<div className={s.message__attachment}>
					<RecommendationAttachment blocks={metadata.blocks} />
				</div>
			</>
		)
	}

	return (
		<div
			className={`${s.message__text} ${msg.from === 'admin' ? s.admin : ''}`}
		>
			{msg.text}
		</div>
	)
}

export const ChatMessage = ({
	selectedUser,
	messages,
	setNewMessage,
	handleSend,
	newMessage,
	onBack,
	onMarkAsRead,
	isStudentInfoCollapsed = false,
	onToggleStudentInfo,
	isLoadingMessages,
}) => {
	const navigate = useNavigate()
	const inputRef = useRef(null)
	const {
		isMobileLg,
		lastActivityLabel,
		isOnline,
		headerAvatarSize,
		messageAvatarSize,
		processedMessages,
		messagesEndRef,
	} = useChatMessage({ selectedUser, messages, onMarkAsRead })

	const studentId = selectedUser?.id
	const canOpenStudent = Boolean(String(studentId || '').trim())
	const openStudentProfile = e => {
		e?.stopPropagation?.()
		navigateToStudentProfile(navigate, studentId)
	}

	return (
		<div
			className={`${s.wrap} ${isStudentInfoCollapsed ? s.studentInfoCollapsed : ''}`}
		>
			{selectedUser ? (
				<>
					<div className={s.header}>
						<div className={s.header__info}>
							{isMobileLg && (
								<div className={s.back} onClick={onBack}>
									<div className={s.back__icon}>
										<ArrowLeft />
									</div>
									<span className={s.back__text}>Students</span>
								</div>
							)}
							<div
								className={s.header__inner}
								onClick={isMobileLg ? onToggleStudentInfo : undefined}
								style={isMobileLg ? { cursor: 'pointer' } : undefined}
							>
								<div className={s.header__photo}>
									<InitialAvatar
										name={selectedUser.name}
										size={headerAvatarSize}
										studentId={selectedUser.id}
										student={selectedUser}
										onClick={
											canOpenStudent ? openStudentProfile : undefined
										}
									/>
								</div>
								<div className={s.header__text}>
									{canOpenStudent ? (
										<button
											type="button"
											className={s.header__name}
											onClick={openStudentProfile}
										>
											{selectedUser.name}
										</button>
									) : (
										<span className={s.header__name}>
											{selectedUser.name}
										</span>
									)}
									{(isOnline || selectedUser.grade) && (
										<div className={s.header__meta}>
											{isOnline && (
												<>
													<span className={s.header__onlineDot} />
													<span className={s.header__metaText}>Online</span>
												</>
											)}
											{isOnline && selectedUser.grade && (
												<span className={s.header__metaDivider}>•</span>
											)}
											{selectedUser.grade && (
												<span className={s.header__metaText}>
													Grade {selectedUser.grade}
												</span>
											)}
										</div>
									)}
								</div>
							</div>
						</div>
						<div className={s.header__info}>
							<div className={s.date}>
								<div className={s.date__icon}>
									<LaptopIcon />
								</div>
								<span className={s.date__text}>
									Last seen:{' '}
									<span className={s.date__val}>{lastActivityLabel}</span>
								</span>
							</div>
							{isStudentInfoCollapsed && onToggleStudentInfo && !isMobileLg && (
								<button
									type="button"
									className={s.header__toggleInfo}
									onClick={onToggleStudentInfo}
									aria-label="Show student info"
								>
									<HeaderIcon />
								</button>
							)}
						</div>
					</div>

					<div className={s.messages}>
						{!!processedMessages?.length ? (
							processedMessages.map((msg, index) => (
								<React.Fragment key={msg.id}>
									{msg.showNewSeparator && (
										<div className={s.newSeparator}>
											<span className={s.newSeparator__text}>New Messages</span>
										</div>
									)}
									{msg.showDateSeparator && msg.dateSeparatorLabel && (
										<div className={s.dateSeparator}>
											<span className={s.dateSeparator__text}>
												{msg.dateSeparatorLabel}
											</span>
										</div>
									)}
									<div
										className={`${s.message} ${
											msg.from === 'admin' ? s.admin : ''
										} ${!msg.isRead && msg.from === 'user' ? s.unread : ''}`}
									>
										{msg.from === 'user' && (
											<div className={s.message__photo}>
												<InitialAvatar
													name={selectedUser.name}
													size={messageAvatarSize}
													studentId={selectedUser.id}
													student={selectedUser}
													onClick={
														canOpenStudent ? openStudentProfile : undefined
													}
												/>
											</div>
										)}
										<div
											className={`${s.message__content} ${
												msg.from === 'admin' ? s.admin : ''
											}`}
										>
											<MessageBody msg={msg} />
											{msg.sendError && msg.sendErrorText && (
												<div className={s.message__sendError}>
													{msg.sendErrorText}
												</div>
											)}
											{msg.from === 'admin' &&
												msg.showStatusRow &&
												msg.seenLabel && (
													<div
														className={`${s.message__meta} ${
															msg.from === 'admin' ? s.admin : ''
														}`}
													>
														<span className={s.message__seen}>
															{msg.seenLabel}
														</span>
													</div>
												)}
										</div>
									</div>
								</React.Fragment>
							))
						) : isLoadingMessages ? (
							<div className={s.skeleton}>
								<div className={`${s.message} ${s.admin}`}>
									<div
										className={`${s.message__content} ${s.admin} ${s.skeleton__content}`}
									>
										<div
											className={`${s.message__text} ${s.admin} ${s.admin} ${s.skeleton__text}`}
										>
											<LoaderImg />
										</div>
									</div>
								</div>
								<div className={`${s.message} `}>
									<div className={s.message__photo}>
										<div className={s.skeleton__avatar}>
											<LoaderImg />
										</div>
									</div>
									<div
										className={`${s.message__content} ${s.skeleton__content}`}
									>
										<div className={`${s.message__text} ${s.skeleton__text}`}>
											<LoaderImg />
										</div>
									</div>
								</div>
							</div>
						) : (
							'No messages yet.'
						)}
						<div ref={messagesEndRef} />
					</div>

					<div className={s.composer}>
						<div
							className={s.composer__form}
							onClick={() => inputRef.current?.focus()}
						>
							<textarea
								ref={inputRef}
								className={s.input}
								placeholder="Type your message here"
								value={newMessage}
								onChange={e => setNewMessage(e.target.value)}
								onKeyDown={e => {
									if (e.key === 'Enter') handleSend()
								}}
							/>
							<div className={s.composer__actions}>
								<button
									type="button"
									onClick={handleSend}
									className={s.send}
								>
									<span className={s.send__text}>Send</span>
									<span className={s.send__icon}>
										<SendIcon />
									</span>
								</button>
							</div>
						</div>
					</div>
				</>
			) : (
				<div className={s.empty}>Select a user to view messages.</div>
			)}
		</div>
	)
}
